import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Stack, FormControl, InputLabel, Select, MenuItem,
  Checkbox, FormControlLabel, Alert, Divider,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { supabase } from '../../supabaseClient.js';
import useAlert from '../../alert/useAlert.js';
import { getTeamLocations, updateTeamLocations } from '../../services/organizationService.js';
import { HK_DISTRICTS } from '../../constants/districts.js';
import OnboardingLayout from './OnboardingLayout.js';

const TEAL = '#29C0BB';

const emptyLocation = () => ({
  id: crypto.randomUUID(),
  district: '',
  address: '',
});

const ModeCard = ({ icon, title, caption, selected, disabled, onSelect }) => (
  <Box
    onClick={() => !disabled && onSelect()}
    sx={{
      flex: 1,
      border: selected ? `2px solid ${TEAL}` : '1px solid #d7dde3',
      borderRadius: 3,
      p: 2.5,
      textAlign: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      backgroundColor: '#fff',
    }}
  >
    <Box sx={{ color: TEAL, mb: 1 }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Typography>
    <Typography variant="body2" sx={{ color: '#5b6770', mt: 0.5 }}>{caption}</Typography>
  </Box>
);

/**
 * Step 3 — optional. Admins can add new team locations; members pick from the
 * locations their admin already added. The user's own selection lands in
 * user_accounts.clinic_locations (with selectedDays left for step 5's
 * Availability card). Service formats are account-level only, edited in the
 * Session fees dialog (step 5) — locations carry no physical/online flags.
 */
const Step3ClinicLocation = ({ ctx, onContinue, onSkip, onStepClick }) => {
  const { userId, initials, org } = ctx;
  const { setAlert } = useAlert();
  const isAdmin = org?.role === 'Admin';

  const [mode, setMode] = useState('new'); // 'new' | 'existing' | 'none'
  const [locationCount, setLocationCount] = useState(1);
  const [locations, setLocations] = useState([emptyLocation()]);
  const [teamLocations, setTeamLocations] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) setMode('existing');
  }, [isAdmin]);

  useEffect(() => {
    const load = async () => {
      if (!org?.id) return;
      const { data } = await getTeamLocations(org.id);
      setTeamLocations(data || []);
    };
    load();
  }, [org]);

  useEffect(() => {
    setLocations((prev) => {
      const next = [...prev];
      while (next.length < locationCount) next.push(emptyLocation());
      return next.slice(0, locationCount);
    });
  }, [locationCount]);

  const updateLocation = (index, field, value) => {
    setLocations((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const saveUserSelection = async (selection) => {
    const clinicLocations = selection.map((loc) => ({
      location_id: loc.id,
      team_id: org.id,
      district: loc.district,
      address: loc.address,
      selectedDays: loc.selectedDays || {},
    }));
    const { error } = await supabase
      .from('user_accounts')
      .update({ clinic_locations: clinicLocations })
      .eq('id', userId);
    return error;
  };

  const handleContinue = async () => {
    if (mode === 'none') {
      onContinue();
      return;
    }
    if (!org?.id) {
      setAlert('Join or create an organization first (step 1).', 'error');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'new') {
        const valid = locations.filter((l) => l.district && l.address.trim());
        if (valid.length === 0) {
          setAlert('Please fill in district and address for at least one location, or skip this step.', 'error');
          return;
        }
        const merged = [...teamLocations, ...valid];
        const { error } = await updateTeamLocations(org.id, userId, merged);
        if (error) {
          setAlert(error.message, 'error');
          return;
        }
        const userError = await saveUserSelection(valid);
        if (userError) {
          setAlert('Locations saved, but linking them to your profile failed. You can redo this in Update Profile.', 'warning');
        }
      } else {
        const chosen = teamLocations.filter((l) => selectedExisting[l.id]);
        if (chosen.length === 0) {
          setAlert('Select at least one location, or skip this step.', 'error');
          return;
        }
        const userError = await saveUserSelection(chosen);
        if (userError) {
          setAlert('Unable to save your locations. Please try again.', 'error');
          return;
        }
      }
      onContinue();
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingLayout
      step={3}
      title="Clinic location"
      description="Add one or more clinic locations where you see clients in person. If you provide services online only or don't have a clinic location yet, you can skip this step."
      sidebarExtra={
        <Box sx={{ p: 2, backgroundColor: '#f0fbfa', borderRadius: 2, display: 'flex', gap: 1 }}>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: TEAL, mt: '2px' }} />
          <Typography variant="body2" sx={{ color: '#5b6770' }}>
            <strong>This step is optional.</strong> You can add or edit clinic locations later in
            Organization settings.
          </Typography>
        </Box>
      }
      avatarInitials={initials}
      onStepClick={onStepClick}
      onSkip={onSkip}
      skipCaption="You can add locations later"
      onContinue={handleContinue}
      continueLabel="Save and continue"
      saving={saving}
    >
      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Add clinic locations (optional)</Typography>
      <Typography variant="body2" sx={{ color: '#5b6770', mb: 2 }}>
        You can add multiple locations. Add at least one if you see clients in person.
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
        How would you like to set up your clinic location?
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <ModeCard
          icon={<AddCircleOutlineIcon fontSize="large" />}
          title="Add a new location"
          caption="Enter the details of your clinic location."
          selected={mode === 'new'}
          disabled={!isAdmin}
          onSelect={() => setMode('new')}
        />
        <ModeCard
          icon={<SearchIcon fontSize="large" />}
          title="Choose an existing location"
          caption="Select from clinic locations already added by your admin."
          selected={mode === 'existing'}
          onSelect={() => setMode('existing')}
        />
        <ModeCard
          icon={<MailOutlineIcon fontSize="large" />}
          title="No location yet"
          caption="I provide online services only or will add it later."
          selected={mode === 'none'}
          onSelect={() => setMode('none')}
        />
      </Stack>

      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f6f8f9', borderRadius: 2, display: 'flex', gap: 1 }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: TEAL, mt: '2px' }} />
        <Typography variant="body2" sx={{ color: '#5b6770' }}>
          Can't find your location? Your admin may not have added it yet. You can also ask your admin
          to invite you to join the organization through email and access the location they've set up.
        </Typography>
      </Box>

      {mode === 'new' && (
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>How many locations do you have?</Typography>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select value={locationCount} onChange={(e) => setLocationCount(e.target.value)}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={n}>{n} location{n > 1 ? 's' : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ color: '#8a94a0' }}>
              You can add more locations anytime later.
            </Typography>
          </Stack>

          {locations.map((loc, index) => (
            <Box key={loc.id} sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography sx={{ color: TEAL, fontWeight: 700, mb: 1.5 }}>Location {index + 1}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Clinic name</Typography>
              <TextField fullWidth size="small" value={org?.name || ''} disabled helperText="Locations belong to your organization." />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>District / Area *</Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select district / area</InputLabel>
                    <Select
                      value={loc.district}
                      label="Select district / area"
                      onChange={(e) => updateLocation(index, 'district', e.target.value)}
                    >
                      {HK_DISTRICTS.map((d) => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Address *</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter full address"
                    value={loc.address}
                    onChange={(e) => updateLocation(index, 'address', e.target.value)}
                    helperText="Include building name, street, floor, and unit number (if applicable)."
                  />
                </Box>
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      {mode === 'existing' && (
        <Box sx={{ mt: 3 }}>
          {teamLocations.length === 0 ? (
            <Alert severity="info">
              Your organization has no locations yet.
              {isAdmin ? ' Choose "Add a new location" to create one.' : ' Ask your admin to add locations in Organization settings.'}
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {teamLocations.map((loc) => (
                <FormControlLabel
                  key={loc.id}
                  control={
                    <Checkbox
                      checked={Boolean(selectedExisting[loc.id])}
                      onChange={(e) => setSelectedExisting((prev) => ({ ...prev, [loc.id]: e.target.checked }))}
                      sx={{ color: TEAL, '&.Mui-checked': { color: TEAL } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {org?.name}{loc.district ? ` — ${loc.district}` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#5b6770' }}>{loc.address}</Typography>
                    </Box>
                  }
                  sx={{ border: '1px solid #d7dde3', borderRadius: 2, m: 0, px: 1.5, py: 1 }}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}
    </OnboardingLayout>
  );
};

export default Step3ClinicLocation;
